import {
  Form,
  Button,
  Flex,
  Space,
  Checkbox,
  Card,
  Input,
  Drawer,
  Avatar,
  Spin,
  Typography,
} from 'antd';
import InputFormBox from 'components/bank/AGBBank/InputFormBox';
import NoComponent from 'components/bank/AGBBank/NoComponent';
import DynamicForm from 'components/ossix/DynamicForm';
import AccountDropdown from 'components/shared/AccountDropdown';
import ContactDropdown, {
  ImageWithFallback,
} from 'components/shared/ContactDropdown';
import CountryDropdown from 'components/shared/CountryDropdown';
import OssixProvidersDropdown from 'components/shared/OssixProvidersDropdown';
import TransferConfirmation from 'components/shared/TransferConfirmation';
import { useLanguage } from 'hooks/Language/useLanguage';
import { ITransferForm } from 'interfaces/components/pageProps';
import { ICountryInfo } from 'interfaces/otherInterface/supportedCountry';
import { IConfirmationData } from 'interfaces/queries/transactions';
import { CloseOutlined } from '@ant-design/icons';
import { ActionType, TargetType } from 'types/generalType';
import agbColors from 'constants/Colors/agbColors';
import { maxSize } from 'constants/screenSize';
import { DrawerStyles } from 'antd/es/drawer/DrawerPanel';

const { Text } = Typography;

const InternationalTransfer: React.FC<ITransferForm> = ({
  selectAccountNumber,
  form,
  step,
  onPrevious,
  accountNumber,
  setAccountNumber,
  loadingAccount,
  recipientAccountNumber,
  onHandleDisplaySelectContact,
  displaySelectContact,
  isLoadingContacts,
  contactAccount,
  setContactAccount,
  onHandleCloseSelectContact,
  setSelectedProvider,
  dynamicFormItem,
  countries,
  loadingCountry,
  setSelectCountry,
  selectCountry,
  selectedProvider,
  handleCloseOpenDrawer,
  isDrawerOpen,
}) => {
  const {
    state,
    fields,
    meta,
    handleInputChange,
    loadVerifyAccount,
    onHandleVerifyAccount,
    filterCountry,
    isVerifyAccountSuccess,
    contextHolder,
    onHandleValueChange,
    onHandleTransferConfirmation,
    loadTransferConfirmation,
    transferConfirmationData,
    onPINValuesChange,
    onHandleConfirmTransfer,
    loadSendFundsToGateway,
    userData,
    formData,
    fixedInputValue,
    setFixedInputValue,
    onChangeFixedInputValue,
    selectedBeneficiary,
    setSelectedBeneficiary,
    onHandleSaveContact,
  } = dynamicFormItem;
  const { translate } = useLanguage();

  const convertConfirmationData = (data: IConfirmationData) => {
    return [
      { label: 'Amount to be sent', value: data?.Amount },
      { label: 'Fees', value: data?.Fees },
      { label: 'Exchange fees', value: data?.ExchangeFees },
      { label: 'Exchange rate', value: data?.ExchangeRate },
      { label: 'Taxes', value: data?.Taxes },
      { label: 'Total amount', value: data?.TotalAmount },
    ];
  };

  const confirmationItems = convertConfirmationData(
    transferConfirmationData,
  );

  const drawerStyles: DrawerStyles = {
    header: {
      background: agbColors.navyBlueColor,
      color: agbColors.lightBlueColor,
      padding: '16px 24px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      height: '64px',
    },
    body: {
      padding: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    content: {
      flex: 1,
      overflowY: 'hidden',
      padding: '0',
    },
    mask: {
      background: 'rgba(0, 0, 0, 0.45)',
    },
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="drawer-scrollable-content">
            <InputFormBox
              title={state?.chosenOption}
              onSelectRecipient={onHandleDisplaySelectContact}
              displaySelectRecipient={
                displaySelectContact ? false : true
              }
              select={[
                <Form
                  layout="vertical"
                  style={{ marginBottom: '-10px' }}
                >
                  {accountNumber?.value !== 'undefined' && (
                    <Form.Item label={translate('Transfer from')}>
                      <AccountDropdown
                        account={accountNumber}
                        loading={loadingAccount}
                        options={selectAccountNumber}
                        setAccount={setAccountNumber}
                        size="100%"
                      />
                    </Form.Item>
                  )}

                  {!displaySelectContact && (
                    <Form.Item>
                      <CountryDropdown
                        label={translate(
                          'Select destination country',
                        )}
                        loading={loadingCountry}
                        countries={countries as ICountryInfo[]}
                        setSelectCountry={setSelectCountry}
                        defaultCountry={
                          userData?.[0]?.OperationCountry
                        }
                      />
                    </Form.Item>
                  )}
                </Form>,
              ]}
              formItems={[
                <Form
                  form={form}
                  layout="vertical"
                  key="form"
                  onValuesChange={onHandleValueChange}
                  autoComplete="off"
                  className="transfer-form"
                  initialValues={{ ...formData, remembered: true }}
                >
                  {displaySelectContact && (
                    <div className="beneficiary-selection-container">
                      <Space.Compact style={{ width: '100%' }}>
                        <Form.Item
                          label={
                            <Text strong>
                              {translate('Select a beneficiary')}
                            </Text>
                          }
                          className="beneficiary-dropdown"
                          name="recipient"
                          style={{ width: '100%' }}
                        >
                          <ContactDropdown
                            contact={contactAccount}
                            setContact={setContactAccount}
                            loading={isLoadingContacts}
                            options={recipientAccountNumber}
                          />
                        </Form.Item>
                        <Form.Item label=" ">
                          <Button
                            size="large"
                            type="primary"
                            ghost
                            onClick={onHandleCloseSelectContact}
                            icon={<CloseOutlined />}
                          />
                        </Form.Item>
                      </Space.Compact>
                    </div>
                  )}
                  {selectedBeneficiary && (
                    <Card
                      size="small"
                      className="beneficiary-card"
                      style={{
                        background:
                          agbColors.$lightNayBlueColor10Percent,
                        width: '100%',
                        maxWidth: maxSize,
                        marginBottom: '20px',
                      }}
                    >
                      <Flex gap="small" align="center">
                        <Avatar
                          size={48}
                          shape="square"
                          src={
                            <ImageWithFallback
                              src={selectedBeneficiary?.Logo}
                              firstName={
                                selectedBeneficiary?.FirstName
                              }
                              lastName={selectedBeneficiary?.LastName}
                            />
                          }
                          className="beneficiary-avatar"
                        />
                        <Flex vertical>
                          <Flex gap="small" align="center">
                            <Text strong>{translate('Name')}:</Text>
                            <Text>
                              {selectedBeneficiary?.FirstName}{' '}
                              {selectedBeneficiary?.LastName}
                            </Text>
                          </Flex>
                          <Flex gap="small" align="center">
                            <Text strong>
                              {translate('Account Number')}:
                            </Text>
                            <Text>
                              {selectedBeneficiary?.AccountNumber}
                            </Text>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Card>
                  )}

                  {!selectedBeneficiary && (
                    <Form.Item
                      label={
                        <Text>{translate('Select operator')}</Text>
                      }
                      className="operator-dropdown"
                    >
                      <OssixProvidersDropdown
                        style={{ width: '100%' }}
                        getSelectedProvider={setSelectedProvider}
                        selectedProvider={selectedProvider}
                        beneficiaryData={selectedBeneficiary}
                        setSelectedBeneficiary={
                          setSelectedBeneficiary
                        }
                        onCloseSelectContactDropdown={
                          onHandleCloseSelectContact
                        }
                        payload={{
                          ActionType: ActionType.Transfer,
                          CountryCode:
                            selectCountry?.CountryCode ||
                            userData?.OperationCountry ||
                            userData?.[0]?.OperationCountry,
                          TargetType: TargetType.Bank,
                        }}
                        showDefaultValue
                      />
                    </Form.Item>
                  )}

                  {!selectedBeneficiary && (
                    <div className="dynamic-form-container">
                      <DynamicForm
                        meta={meta}
                        form={form}
                        inputs={fields}
                        provider={selectedProvider}
                        amountRequired={true}
                        verifyAccount={meta?.verifyAccount}
                        handleInputChange={handleInputChange}
                        countries={filterCountry}
                        defaultCountry={
                          selectCountry?.CountryCode ||
                          userData?.OperationCountry ||
                          userData?.[0]?.OperationCountry
                        }
                        fixedInputValue={fixedInputValue}
                        setFixedInputValue={setFixedInputValue}
                        onChangeFixedInputValue={
                          onChangeFixedInputValue
                        }
                        isVerifyAccountSuccess={
                          isVerifyAccountSuccess
                        }
                      />
                    </div>
                  )}

                  {isVerifyAccountSuccess &&
                    !displaySelectContact && (
                      <Form.Item className="save-contact-option">
                        <Checkbox onChange={onHandleSaveContact}>
                          {translate('Save Bank Account')}
                        </Checkbox>
                      </Form.Item>
                    )}

                  {selectedBeneficiary && (
                    <Form.Item
                      name="Amount"
                      label={
                        <Text strong>{translate('Amount')}</Text>
                      }
                      className="amount-input"
                    >
                      <Input
                        type="number"
                        size="large"
                        name="Amount"
                        placeholder={translate(
                          'Enter the amount here',
                        )}
                        min={0}
                        step="0.01"
                      />
                    </Form.Item>
                  )}
                </Form>,
              ]}
            />
          </div>
        );
      case 2:
        return (
          <div className="drawer-scrollable-content">
            <TransferConfirmation
              isLoading={loadSendFundsToGateway}
              items={confirmationItems}
              onHandleBack={onPrevious}
              onHandleConfirm={onHandleConfirmTransfer}
              onValuesChange={onPINValuesChange}
              hideButtons={true}
            />
          </div>
        );
      default:
        return (
          <div className="empty-state">
            <NoComponent />
          </div>
        );
    }
  };

  const renderStep1Actions = () => (
    <div className="drawer-footer">
      {!selectedBeneficiary &&
        !isVerifyAccountSuccess &&
        !meta?.verifyAccount && (
          <Button
            type="primary"
            onClick={onHandleTransferConfirmation}
            loading={loadTransferConfirmation}
            disabled={!fixedInputValue ? true : false}
            block
          >
            {translate('Proceed')}
          </Button>
        )}

      {!selectedBeneficiary &&
        !isVerifyAccountSuccess &&
        meta?.verifyAccount && (
          <Button
            type="primary"
            onClick={onHandleVerifyAccount}
            loading={loadVerifyAccount}
            block
          >
            {translate('Next')}
          </Button>
        )}

      {isVerifyAccountSuccess && (
        <Button
          type="primary"
          onClick={onHandleTransferConfirmation}
          loading={loadTransferConfirmation}
          disabled={!fixedInputValue ? true : false}
          block
        >
          {translate('Proceed')}
        </Button>
      )}

      {selectedBeneficiary && (
        <Button
          type="primary"
          onClick={onHandleTransferConfirmation}
          loading={loadTransferConfirmation}
          block
        >
          {translate('Proceed')}
        </Button>
      )}
    </div>
  );

  const renderFooter = () => {
    switch (step) {
      case 1:
        return renderStep1Actions();
      case 2:
        return (
          <div className="drawer-footer dual-button">
            <Button
              variant="filled"
              color="volcano"
              type="primary"
              onClick={onPrevious}
              size="large"
            >
              {translate('Back')}
            </Button>
            <Button
              type="primary"
              onClick={onHandleConfirmTransfer}
              loading={loadSendFundsToGateway}
              size="large"
            >
              {translate('Confirm')}
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {contextHolder}
      <Drawer
        width={500}
        open={isDrawerOpen?.open}
        title={
          <Text
            style={{ color: 'white', fontSize: 16, fontWeight: 500 }}
          >
            {translate(isDrawerOpen?.drawerTitle)}
          </Text>
        }
        styles={drawerStyles}
        closeIcon={false}
        onClose={handleCloseOpenDrawer}
        className="transfer-mno-drawer"
        rootClassName="transfer-mno-drawer-root"
        extra={
          <Button
            type="text"
            onClick={handleCloseOpenDrawer}
            icon={<CloseOutlined style={{ color: 'white' }} />}
            className="drawer-close-button"
          />
        }
      >
        {isDrawerOpen?.open ? (
          <div className="drawer-layout-container">
            {renderStep()}
            {renderFooter()}
          </div>
        ) : (
          <Flex
            justify="center"
            align="center"
            style={{ height: '100%' }}
          >
            <Spin size="large" />
          </Flex>
        )}
      </Drawer>
    </>
  );
};

export default InternationalTransfer;
